import React, { useEffect, useRef, useState } from "react";
import ClubsInfo from "../../ClubsInfo";
import PaginationComponent from "../../../ui/PaginationComponent";
import { useLoading } from "../../../contexts/LoadingContext";
import IApiResponse from "../../../types/IApiResponse";
import { IClubData } from "../../../types/IPlayerDetail";
import { postData } from "../../../services/api";
import apiUrls from "../../../config/apiUrls";
import handleApiResponse from "../../../utils/handleApiResponse";
import LabelText from "../../../ui/LabelText";
import styled from "styled-components";
import { useClubSearch } from "../../../contexts/ClubSearchContext";
import SecondaryButton from "../../../ui/SecondaryButton";
import FilterSort from "../../PlayerLanding/PlayerSearch/FilterSort";
import { useNotification } from "../../../contexts/NotificationContext";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PWNotification from "../../PWNotification";
import { SortDropdown } from "../../../styled-components/FilterStyles";
import MachineFootballIcon from "../../../assets/sidebarIcons/MachineFootballIcon.png";
import LinearProgress, { linearProgressClasses } from "@mui/material/LinearProgress";
import { styled as MaterialStyled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
`;

const BorderLinearProgress = MaterialStyled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[300],
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#00D1A8",
    ...theme.applyStyles("dark", {
      backgroundColor: "#308fe8",
    }),
  },
}));

const ClubSearchResult = () => {
  const {
    isClubFilterChange,
    clubSearchData,
    clubSearchText,
    setClubSearchData,
    setClubSearchText,
    regionAreaData,
    setRegionAreaData,
    clubfilterValues,
    setClubFilterValues,
    setIsClubFilterChange,
    isRevenueAnnual,
    isWageAnnual,
    sortClubOrderName,
    sortClubOrdering,
    currentPage,
    setCurrentPage,
  } = useClubSearch();
  const { showNotification, hideNotification, setDisplayNotification, displayNotification, notificationContent, setNotificationContent } =
    useNotification();
  const { loading, startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(0);
  const [isPageChange, setIsPageChange] = useState<boolean>(false);
  const [clubResults, setClubResults] = useState<IClubData>();
  const [clubResultsCount, setClubResultsCount] = useState(0);
  const [isSortClicked, setIsSortClicked] = useState(false);
  const filterSortRef = useRef<HTMLDivElement | null>(null);
  const sortByPositionData = ["Total revenue", "Average wage", "Average player age"];
  const [loadingProgressValue, setLoadingProgressValue] = useState<number>(0);
  const [isClubResultsLoading, setIsClubResultsLoading] = useState<boolean>(false);
  const [subProcessIndex, setSubProcessIndex] = useState<number>(0);
  const [teamIds , setTeamIds] = useState<number[]>([])
  const subProcesses = ["Retrieving team metadata to create the list", "Applying filters", "Sorting the data", "Nearly complete"];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterSortRef.current && !filterSortRef.current.contains(event.target as Node)) {
        setIsSortClicked(false); // Close FilterSort if clicked outside
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterSortRef]);

  const handleToggle = (index: number) => {
    setOpenIndex((prevIndex) => (prevIndex === index ? -1 : index));
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setIsPageChange(true);
  };

  useEffect(() => {
    const fetchClubResults = async () => {
      try {
        startLoading();
        const ApiRequestData = {
          pageNo: currentPage,
          pageSize: 20,
          orderName: sortClubOrderName,
          ordering: sortClubOrdering,
          isRevenueAnnual: isRevenueAnnual,
          isWageAnnual: isWageAnnual,
          minRevenuePerWeek: clubfilterValues.minRevenuePerWeek,
          maxRevenuePerWeek: clubfilterValues.maxRevenuePerWeek,
          minRevenueAnnual: clubfilterValues.minRevenueAnnual,
          maxRevenueAnnual: clubfilterValues.maxRevenueAnnual,
          minAvWagePerWeek: clubfilterValues.minAvWagePerWeek,
          maxAvWagePerWeek: clubfilterValues.maxAvWagePerWeek,
          minAvWageAnnual: clubfilterValues.minAvWageAnnual,
          maxAvWageAnnual: clubfilterValues.maxAvWageAnnual,
          areaId: clubfilterValues.areaId,
        };
        const response: IApiResponse<IClubData> = await postData(apiUrls.GetTeamsByFilterSearch, ApiRequestData);
        handleApiResponse(
          response,
          (clubData) => {
            stopLoading();
            if (clubData.teams.length > 0) {
              setDisplayNotification(false);
              setNotificationContent(null);
              setClubResults(clubData);
              setClubResultsCount(clubData.count);
              setIsPageChange(false);
              setTeamIds(clubData.teamIds)
            } else {
              setDisplayNotification(true);
              setNotificationContent(
                <div className="notification">
                  <InfoOutlinedIcon className="icon" />
                  <p>
                    <LabelText className="squad-chart-tooltip notification-content">No results found -</LabelText>
                    <LabelText className="player-given-name notification-content"> Try adjusting the filters</LabelText>
                  </p>
                </div>
              );
              setClubResults(undefined);
              setClubResultsCount(0);
            }
          },
          (error) => {
            stopLoading();
            console.log("Error in handleApiResponse callback:", error);
          }
        );
      } catch (error) {
        stopLoading();
        console.error("Error in fetchClubResults:", error);
      }
    };
    fetchClubResults();
  }, [clubfilterValues, currentPage, sortClubOrderName, sortClubOrdering, isClubFilterChange, isRevenueAnnual, isWageAnnual]);

  const totalPages = Math.ceil(clubResultsCount / 20);

  const renderClubResults = () => {
    return (
      <>
        {clubResults?.teams.map((clubData, index) => (
          <div className="player-card-wrapper">
            <ClubsInfo
              clubData={clubData}
              isOpen={openIndex === index}
              onToggle={(event: React.MouseEvent) => {
                event.stopPropagation();
                handleToggle(index);
              }}
            />
          </div>
        ))}
        <div className="pagination-wrapper">
          <PaginationComponent totalPages={totalPages} currentPage={currentPage} onPageChange={handlePageChange} />
        </div>
      </>
    );
  };

  const handleSortChange = () => {
    setIsSortClicked(false);
  };

  useEffect(() => {
    if ((!clubResults && !displayNotification) || loading) {
      setIsClubResultsLoading(true);
      const intervalId = setInterval(() => {
        setLoadingProgressValue((prev) => {
          if (prev >= 75) {
            clearInterval(intervalId);
            return prev;
          }
          return prev + 25;
        });
        setSubProcessIndex((prev) => {
          if (prev < 3) {
            return prev + 1;
          } else {
            return 3;
          }
        });
      }, 5000);
      return () => clearInterval(intervalId);
    } else {
      setSubProcessIndex(3);
      setLoadingProgressValue(100);
      setTimeout(() => {
        setIsClubResultsLoading(false);
        setSubProcessIndex(0);
        setLoadingProgressValue(0);
      }, 1000);
    }
  }, [clubResults, displayNotification, loading]);

  useEffect(() => {
    setDisplayNotification(false)
  },[])

  const handleClubComparision = () => {
    localStorage.removeItem('removedclubIds');
    navigate(`/clubcomparison`, { state: {  teamIds, isFromSortList: false }}); 
  }

  return (
    <div className="results-section tab-content-right">
      <div className="header-content-wrapper">
        <StyledHeader className="header-content" style={{ paddingBottom: "3px" }}>
          <LabelText className="player-family-name">Results</LabelText>
          <div style={{display:'flex'}}>
          <SecondaryButton iconType="sort" label={`Sort By: ${sortClubOrderName}`} onClick={() => setIsSortClicked((prev) => !prev)} />
          {isSortClicked && (
            <SortDropdown ref={filterSortRef} className="newplayer-brop">
              <FilterSort onSortChange={handleSortChange} data={sortByPositionData} sortType={"Club"} />
            </SortDropdown>
          )}
          {/* <div className="comparision-action-div">
                <SecondaryButton
                  iconType="playercomparison"
                  label="Comparison tools"
                  className="comparision-tools-btn"
                  onClick={handleClubComparision}
                />
              </div> */}
              </div>
        </StyledHeader>
      </div>
      {isClubResultsLoading && (
        <div className="playerresults-loader">
          <div className="loader-header-container">
            <p>Creating list</p>
            <img src={MachineFootballIcon} className="mf-logo" alt="machine-football-logo" />
          </div>
          <div className="loader-progressbar-container">
            <BorderLinearProgress variant="determinate" value={loadingProgressValue} />
          </div>
          <div className="loader-subprocess">
            {subProcesses[subProcessIndex]}
            <p>{loadingProgressValue}%</p>
          </div>
        </div>
      )}
      {renderClubResults()}
      {displayNotification && (
        <PWNotification
          message={notificationContent}
          setIsNotification={() => setDisplayNotification(false)}
          isDisplayCloseIcon={false}
          className="player-filter-notification"
        />
      )}
    </div>
  );
};

export default ClubSearchResult;
